const YEAR_MIN = 1800;
const YEAR_MAX = 2099;

export const SERIAL_STAT_DESCRIPTIONS = {
  'Star Serial': 'Serial includes a star, indicating a replacement note.',
  'Number 1 Serial': 'Digits are 00000001, the lowest possible serial.',
  'Low Serial (under 2000)': 'Numeric portion is below 2000.',
  'High (over 99999900)': 'Numeric portion is above 99999900.',
  'Birth Year Serial': 'First four digits are 0000 and last four form a plausible year.',
  'Perfect Up Ladder': 'Digits ascend 12345678.',
  'Perfect Down Ladder': 'Digits descend 87654321.',
  'Partial Up Ladder': 'At least four consecutive ascending digits.',
  'Partial Down Ladder': 'At least four consecutive descending digits.',
  'Solid Serial Number': 'All eight digits are the same.',
  'Partial Solid Number': 'At least four identical digits in a row.',
  'Super Repeater': 'First two digits repeat four times (ABABABAB).',
  'Partial Repeater': 'A two-digit pair repeats at least three times.',
  'Double Quad': 'First four digits identical and last four digits identical, but different from first four.',
  'Palindrome (Radar)': 'Digits read the same forward and backward.',
  '7 of a Kind': 'Seven of the eight digits are the same.',
  '7 in a row': 'Seven identical digits appear consecutively.',
  'Radar Repeater': 'Palindrome where first four digits repeat as last four.',
  'Super Radar': 'Palindrome with the middle six digits all identical.',
  "Binary (only 1s & 0s)": 'Digits contain only 0 and 1.',
  'Binary Super Repeater': 'Alternating 01 pattern (01010101 or 10101010).',
};

const toDigits = (serial) => {
  const match = (serial || '').toString().toUpperCase().match(/(\d{8})/);
  return match ? match[1] : null;
};

const isPalindrome = (digits) => digits === digits.split('').reverse().join('');

const getMaxSameRun = (digits) => {
  let maxRun = 1;
  let currentRun = 1;
  for (let i = 1; i < digits.length; i += 1) {
    if (digits[i] === digits[i - 1]) {
      currentRun += 1;
      maxRun = Math.max(maxRun, currentRun);
    } else {
      currentRun = 1;
    }
  }
  return maxRun;
};

const getMaxConsecutiveRun = (digits, step) => {
  let maxRun = 1;
  let currentRun = 1;
  for (let i = 1; i < digits.length; i += 1) {
    const prev = Number(digits[i - 1]);
    const curr = Number(digits[i]);
    if (curr - prev === step) {
      currentRun += 1;
      maxRun = Math.max(maxRun, currentRun);
    } else {
      currentRun = 1;
    }
  }
  return maxRun;
};

const hasRepeatingPair = (digits, minRepeats) => {
  for (let i = 0; i <= digits.length - 2; i += 1) {
    const pair = digits.slice(i, i + 2);
    let count = 1;
    let nextIndex = i + 2;
    while (nextIndex + 2 <= digits.length && digits.slice(nextIndex, nextIndex + 2) === pair) {
      count += 1;
      nextIndex += 2;
    }
    if (count >= minRepeats) {
      return true;
    }
  }
  return false;
};

export const getSerialStats = (serial) => {
  if (!serial) return [];

  const normalized = serial.toString().trim().toUpperCase();
  const stats = [];

  if (normalized.includes('*') || normalized.includes('★')) {
    stats.push('Star Serial');
  }

  const digits = toDigits(normalized);
  if (!digits) return stats;

  const numericValue = Number(digits);
  const lastFour = Number(digits.slice(4));

  const allSame = digits.split('').every((digit) => digit === digits[0]);
  const maxSameRun = getMaxSameRun(digits);

  const isPerfectUp = digits === '12345678';
  const isPerfectDown = digits === '87654321';
  const isSuperRepeater = digits === digits.slice(0, 2).repeat(4);
  const isBinary = /^[01]{8}$/.test(digits);
  const isBinarySuperRepeater = digits === '01010101' || digits === '10101010';
  const isRadar = isPalindrome(digits);

  if (digits === '00000001') stats.push('Number 1 Serial');
  if (numericValue < 2000) stats.push('Low Serial (under 2000)');
  if (numericValue > 99999900) stats.push('High (over 99999900)');
  if (digits.startsWith('0000') && lastFour >= YEAR_MIN && lastFour <= YEAR_MAX) {
    stats.push('Birth Year Serial');
  }

  if (isPerfectUp) stats.push('Perfect Up Ladder');
  if (isPerfectDown) stats.push('Perfect Down Ladder');

  if (!isPerfectUp && getMaxConsecutiveRun(digits, 1) >= 4) {
    stats.push('Partial Up Ladder');
  }
  if (!isPerfectDown && getMaxConsecutiveRun(digits, -1) >= 4) {
    stats.push('Partial Down Ladder');
  }

  if (allSame) stats.push('Solid Serial Number');
  if (!allSame && maxSameRun >= 4) stats.push('Partial Solid Number');

  if (isSuperRepeater) stats.push('Super Repeater');
  if (!isSuperRepeater && hasRepeatingPair(digits, 3)) stats.push('Partial Repeater');

  const isDoubleQuad =
    digits.slice(0, 4).split('').every((d) => d === digits[0]) &&
    digits.slice(4).split('').every((d) => d === digits[4]) &&
    digits[0] !== digits[4];
  if (isDoubleQuad) stats.push('Double Quad');

  if (isRadar) stats.push('Palindrome (Radar)');

  const counts = digits.split('').reduce((acc, digit) => {
    acc[digit] = (acc[digit] || 0) + 1;
    return acc;
  }, {});
  if (Object.values(counts).some((count) => count === 7)) {
    stats.push('7 of a Kind');
  }

  if (maxSameRun === 7) stats.push('7 in a row');

  const isRadarRepeater = isRadar && digits.slice(0, 4) === digits.slice(4);
  if (isRadarRepeater) stats.push('Radar Repeater');

  const isSuperRadar = isRadar && digits.slice(1, 7).split('').every((d) => d === digits[1]);
  if (isSuperRadar) stats.push('Super Radar');

  if (isBinary) stats.push("Binary (only 1s & 0s)");
  if (isBinarySuperRepeater) stats.push('Binary Super Repeater');

  return stats;
};

export default getSerialStats;
